/**
 * Client types for native app authentication.
 *
 * Identifies the type of client making authentication requests.
 * Used for tracking sessions and validating redirect URIs.
 */

export const ClientType = {
  Web: "web",
  Desktop: "desktop",
  Mobile: "mobile",
  Api: "api",
} as const;

export type ClientType = (typeof ClientType)[keyof typeof ClientType];

export const CLIENT_TYPES = Object.values(ClientType);

export function isValidClientType(value: string): value is ClientType {
  return CLIENT_TYPES.includes(value as ClientType);
}

/** Allowed redirect URI scheme prefixes per client type */
const ALLOWED_SCHEME_PREFIXES: Record<ClientType, readonly string[]> = {
  [ClientType.Web]: ["https://"],
  [ClientType.Desktop]: ["animus://"],
  [ClientType.Mobile]: ["exp://", "animus://"],
  [ClientType.Api]: [],
};

/** Additional prefixes allowed in development for localhost OAuth callbacks */
const DEV_LOCALHOST_PREFIXES: readonly string[] = [
  "http://127.0.0.1:",
  "http://localhost:",
];

export function isValidRedirectUri(
  uri: string,
  clientType: ClientType,
  allowDevLocalhost = false
): boolean {
  const prefixes = ALLOWED_SCHEME_PREFIXES[clientType];
  if (prefixes.some((prefix) => uri.startsWith(prefix))) {
    return true;
  }

  if (allowDevLocalhost && clientType === ClientType.Desktop) {
    return DEV_LOCALHOST_PREFIXES.some((prefix) => uri.startsWith(prefix));
  }

  return false;
}
